// AGC: FinancialContext — Compatibility wrapper around TanStack Query hooks
// AGC: Preserves the FinancialProvider + useFinancial() API so all consumers work unchanged
// AGC: Internal data fetching delegates to services/financialService.ts (TanStack Query)
// AGC: In a future cleanup, consumers can import query hooks directly

import React, { createContext, useContext, useCallback, useEffect, ReactNode } from 'react'
import {
  useExpensesQuery,
  useRevenuesQuery,
  useSafrasQuery,
  useCreateExpenseMutation,
  useDeleteExpenseMutation,
  useCreateRevenueMutation,
  useDeleteRevenueMutation,
  useCreateSafraMutation,
  useDeleteSafraMutation,
  type CreateExpenseData,
  type CreateRevenueData,
  type CreateSafraData,
} from '../services/financialService'
import { useAuth } from '../hooks/useAuth'
import type { Expense, Revenue, Safra } from '../types'
import { useQueryClient } from '@tanstack/react-query'

interface FinancialContextType {
  expenses: Expense[]
  revenues: Revenue[]
  safras: Safra[]
  isLoading: boolean
  isSyncing: boolean
  addExpense: (data: CreateExpenseData) => Promise<Expense>
  deleteExpense: (id: string) => Promise<void>
  addRevenue: (data: CreateRevenueData) => Promise<Revenue>
  deleteRevenue: (id: string) => Promise<void>
  addSafra: (data: CreateSafraData) => Promise<Safra>
  deleteSafra: (id: string) => Promise<void>
  refreshFinances: () => Promise<void>
}

const FinancialContext = createContext<FinancialContextType | undefined>(undefined)

export const useFinancial = (): FinancialContextType => {
  const ctx = useContext(FinancialContext)
  if (!ctx) throw new Error('useFinancial must be used within FinancialProvider')
  return ctx
}

interface FinancialProviderProps {
  children: ReactNode
}

export const FinancialProvider: React.FC<FinancialProviderProps> = ({ children }) => {
  const { user } = useAuth()
  const enabled = !!user?.uid
  const qc = useQueryClient()

  // AGC: Query hooks — data fetching
  const { data: expenses = [], isLoading: expLoading, isFetching: expFetching } = useExpensesQuery(enabled)
  const { data: revenues = [], isLoading: revLoading, isFetching: revFetching } = useRevenuesQuery(enabled)
  const { data: safras = [], isLoading: safLoading, isFetching: safFetching } = useSafrasQuery(enabled)

  // AGC: Mutations
  const createExpense = useCreateExpenseMutation()
  const removeExpense = useDeleteExpenseMutation()
  const createRevenue = useCreateRevenueMutation()
  const removeRevenue = useDeleteRevenueMutation()
  const createSafra = useCreateSafraMutation()
  const removeSafra = useDeleteSafraMutation()

  // AGC: Derived state
  const isLoading = expLoading || revLoading || safLoading
  const isSyncing = expFetching || revFetching || safFetching

  // AGC: Actions — bridge to mutations
  const addExpense = useCallback(async (data: CreateExpenseData): Promise<Expense> => {
    return createExpense.mutateAsync(data)
  }, [createExpense])

  const deleteExpense = useCallback(async (id: string): Promise<void> => {
    await removeExpense.mutateAsync(id)
  }, [removeExpense])

  const addRevenue = useCallback(async (data: CreateRevenueData): Promise<Revenue> => {
    return createRevenue.mutateAsync(data)
  }, [createRevenue])

  const deleteRevenue = useCallback(async (id: string): Promise<void> => {
    await removeRevenue.mutateAsync(id)
  }, [removeRevenue])

  const addSafra = useCallback(async (data: CreateSafraData): Promise<Safra> => {
    return createSafra.mutateAsync(data)
  }, [createSafra])

  const deleteSafra = useCallback(async (id: string): Promise<void> => {
    await removeSafra.mutateAsync(id)
  }, [removeSafra])

  const refreshFinances = useCallback(async (): Promise<void> => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['expenses'] }),
      qc.invalidateQueries({ queryKey: ['revenues'] }),
      qc.invalidateQueries({ queryKey: ['safras'] }),
    ])
  }, [qc])

  // AGC: Listen for 'refresh-finances' events dispatched by voice entry and MainMenu
  // AGC: Invalidates all 3 queries so new entries appear immediately
  useEffect(() => {
    const onRefresh = () => { void refreshFinances() }
    window.addEventListener('refresh-finances', onRefresh)
    return () => window.removeEventListener('refresh-finances', onRefresh)
  }, [refreshFinances])

  return (
    <FinancialContext.Provider
      value={{
        expenses,
        revenues,
        safras,
        isLoading,
        isSyncing,
        addExpense,
        deleteExpense,
        addRevenue,
        deleteRevenue,
        addSafra,
        deleteSafra,
        refreshFinances,
      }}
    >
      {children}
    </FinancialContext.Provider>
  )
}
